import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import { projectsService } from '../../services/partnerService';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

const STATUS_OPTIONS = ['Planning', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
const HEALTH_OPTIONS = [
  { label: 'On Track', value: 'on-track' },
  { label: 'At Risk', value: 'at-risk' },
  { label: 'Delayed', value: 'delayed' },
];

const HEALTH_COLORS = {
  'on track': 'bg-success/10 text-success',
  'at risk': 'bg-warning/10 text-warning',
  'delayed': 'bg-error/10 text-error',
};

const STATUS_COLORS = {
  planning: 'bg-secondary/10 text-secondary',
  'in progress': 'bg-primary/10 text-primary',
  'on hold': 'bg-slate-100 text-slate-600',
  completed: 'bg-success/10 text-success',
  cancelled: 'bg-error/10 text-error',
};

const ProjectProfilePage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [project, setProject] = useState(location?.state?.project || null);
  const [loading, setLoading] = useState(!location?.state?.project);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: '',
    client: '',
    value: '',
    project_status: 'Planning',
    health: 'on-track',
    start_date: '',
    end_date: '',
    completion: 0,
    description: '',
  });

  useEffect(() => {
    if (project) {
      setForm({
        name: project?.name || '',
        client: project?.client || '',
        value: project?.rawValue || '',
        project_status: project?.status || 'Planning',
        health: project?.rawHealth || 'on-track',
        start_date: project?.rawStartDate || '',
        end_date: project?.rawEndDate || '',
        completion: project?.progress || 0,
        description: project?.description || '',
      });
    }
  }, [project]);

  useEffect(() => {
    if (!project && id) {
      const fetchProject = async () => {
        setLoading(true);
        try {
          const { data, error } = await supabase
            ?.from('projects')
            ?.select('*, partners(brand_name, legal_name)')
            ?.eq('id', id)
            ?.single();
          if (error) throw error;
          const p = data;
          setProject({
            id: p?.id,
            projectId: `PRJ-${p?.id?.slice(0, 8)?.toUpperCase()}`,
            name: p?.name,
            deliveryRole: 'Joint Delivery',
            timeline: p?.start_date && p?.end_date ? `${new Date(p.start_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${new Date(p.end_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'TBD',
            value: p?.value >= 1000000 ? `OMR ${(p?.value / 1000000)?.toFixed(1)}M` : `OMR ${(p?.value / 1000)?.toFixed(0)}K`,
            rawValue: p?.value,
            status: p?.project_status,
            health: p?.health === 'on-track' ? 'On Track' : p?.health === 'at-risk' ? 'At Risk' : p?.health === 'delayed' ? 'Delayed' : 'On Track',
            rawHealth: p?.health || 'on-track',
            progress: p?.completion,
            partner_id: p?.partner_id || '',
            client: p?.client || '',
            rawStartDate: p?.start_date || '',
            rawEndDate: p?.end_date || '',
            description: p?.description || '',
          });
        } catch (err) {
          toast?.error('Failed to load project');
        } finally {
          setLoading(false);
        }
      };
      fetchProject();
    }
  }, [id]);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSave = async () => {
    if (!form?.name?.trim()) { toast?.error('Project name is required.'); return; }
    setSaving(true);
    try {
      await projectsService?.update(id, {
        name: form?.name,
        client: form?.client,
        value: parseFloat(form?.value) || 0,
        project_status: form?.project_status,
        health: form?.health,
        start_date: form?.start_date || null,
        end_date: form?.end_date || null,
        completion: parseInt(form?.completion) || 0,
        description: form?.description,
      });
      toast?.success('Project updated successfully');
      setProject(prev => ({
        ...prev,
        name: form?.name,
        client: form?.client,
        status: form?.project_status,
        rawHealth: form?.health,
        health: form?.health === 'on-track' ? 'On Track' : form?.health === 'at-risk' ? 'At Risk' : 'Delayed',
        progress: parseInt(form?.completion) || 0,
        rawStartDate: form?.start_date,
        rawEndDate: form?.end_date,
        description: form?.description,
        rawValue: parseFloat(form?.value) || 0,
        value: parseFloat(form?.value) >= 1000000 ? `OMR ${(parseFloat(form?.value) / 1000000)?.toFixed(1)}M` : `OMR ${(parseFloat(form?.value) / 1000)?.toFixed(0)}K`,
      }));
      setIsEditing(false);
    } catch (err) {
      toast?.error(err?.message || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase?.from('projects')?.delete()?.eq('id', id);
      if (error) throw error;
      toast?.success('Project deleted');
      navigate('/engagement-management-hub');
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete project');
      setDeleting(false);
    }
  };

  const statusKey = project?.status?.toLowerCase();
  const healthKey = project?.health?.toLowerCase();
  const statusClass = STATUS_COLORS?.[statusKey] || 'bg-slate-100 text-slate-600';
  const healthClass = HEALTH_COLORS?.[healthKey] || 'bg-slate-100 text-slate-600';
  const healthIcon = project?.health === 'On Track' ? 'CheckCircle2' : project?.health === 'At Risk' ? 'AlertTriangle' : 'XCircle';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-blue-50/20">
        <Header />
        <div className="flex items-center justify-center py-64">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{project?.name || 'Project'} — PartnerHub</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-blue-50/20">
        <Header />
        <main className="px-24 lg:px-32 pt-24 pb-48">
          <div className="max-w-4xl mx-auto">
            {/* Back */}
            <button
              onClick={() => navigate('/engagement-management-hub')}
              className="flex items-center gap-8 text-sm text-slate-500 hover:text-slate-800 mb-24 transition-all"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Engagement Hub
            </button>

            {/* Header Card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-28 mb-20">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-16 mb-20">
                <div className="flex items-center gap-16">
                  <div className="p-14 bg-gradient-to-br from-accent to-primary rounded-xl shadow-md">
                    <Icon name="FolderOpen" size={28} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900">{project?.name}</h1>
                    <p className="text-sm text-slate-500 mt-4">{project?.projectId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  {!isEditing && (
                    <>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-6 px-14 py-8 text-sm font-medium border border-slate-200 rounded-lg hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700 transition-all"
                      >
                        <Icon name="Pencil" size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center gap-6 px-14 py-8 text-sm font-medium border border-slate-200 rounded-lg hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-all"
                      >
                        <Icon name="Trash2" size={14} />
                        Delete
                      </button>
                    </>
                  )}
                  {isEditing && (
                    <>
                      <button onClick={() => setIsEditing(false)} className="px-14 py-8 text-sm font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-all">Cancel</button>
                      <button onClick={handleSave} disabled={saving} className="flex items-center gap-6 px-14 py-8 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-all">
                        {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="Check" size={14} />}
                        Save Changes
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-8">
                <span className={`inline-flex items-center px-10 py-5 rounded-full text-xs font-semibold ${statusClass}`}>{project?.status}</span>
                <span className={`inline-flex items-center gap-4 px-10 py-5 rounded-full text-xs font-semibold ${healthClass}`}>
                  <Icon name={healthIcon} size={12} />
                  {project?.health}
                </span>
              </div>
            </div>

            {/* Delete Confirm */}
            {showDeleteConfirm && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-24 mb-20 flex flex-col sm:flex-row sm:items-center gap-16">
                <div className="flex items-center gap-12 flex-1">
                  <div className="p-10 bg-red-100 rounded-full">
                    <Icon name="AlertTriangle" size={20} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-red-800">Delete this project?</p>
                    <p className="text-xs text-red-600 mt-2">This action cannot be undone.</p>
                  </div>
                </div>
                <div className="flex gap-8">
                  <button onClick={() => setShowDeleteConfirm(false)} className="px-16 py-8 text-sm border border-red-200 rounded-lg text-red-700 hover:bg-red-100 transition-all">Cancel</button>
                  <button onClick={handleDelete} disabled={deleting} className="px-16 py-8 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 transition-all">
                    {deleting ? 'Deleting...' : 'Confirm Delete'}
                  </button>
                </div>
              </div>
            )}

            {/* Details Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">
              <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-24">
                <h2 className="text-base font-semibold text-slate-800 mb-20 flex items-center gap-8">
                  <Icon name="Info" size={16} className="text-primary" />
                  Project Details
                </h2>
                {isEditing ? (
                  <div className="space-y-16">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-6">Project Name <span className="text-error">*</span></label>
                      <input name="name" value={form?.name} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div className="grid grid-cols-2 gap-12">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Status</label>
                        <select name="project_status" value={form?.project_status} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
                          {STATUS_OPTIONS?.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Health</label>
                        <select name="health" value={form?.health} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
                          {HEALTH_OPTIONS?.map(h => <option key={h?.value} value={h?.value}>{h?.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-12">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Client</label>
                        <input name="client" value={form?.client} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Value (OMR)</label>
                        <input name="value" type="number" value={form?.value} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-12">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Start Date</label>
                        <input name="start_date" type="date" value={form?.start_date} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">End Date</label>
                        <input name="end_date" type="date" value={form?.end_date} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-6">Completion (%)</label>
                      <input name="completion" type="number" min="0" max="100" value={form?.completion} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-6">Description</label>
                      <textarea name="description" value={form?.description} onChange={handleChange} rows={4} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-16">
                    <div className="grid grid-cols-2 gap-16">
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Client</p>
                        <p className="text-sm font-medium text-slate-800">{project?.client || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Project Value</p>
                        <p className="text-sm font-semibold text-slate-800">{project?.value || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Timeline</p>
                        <p className="text-sm font-medium text-slate-800">{project?.timeline || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Delivery Role</p>
                        <p className="text-sm font-medium text-slate-800">{project?.deliveryRole || '—'}</p>
                      </div>
                    </div>
                    {project?.description && (
                      <div className="pt-16 border-t border-slate-100">
                        <p className="text-xs text-slate-500 mb-8">Description</p>
                        <p className="text-sm text-slate-700 leading-relaxed">{project?.description}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Progress */}
              <div className="space-y-16">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-24">
                  <h2 className="text-base font-semibold text-slate-800 mb-16 flex items-center gap-8">
                    <Icon name="BarChart2" size={16} className="text-accent" />
                    Progress
                  </h2>
                  <div className="flex items-end gap-8 mb-12">
                    <span className="text-4xl font-bold text-slate-900">{project?.progress}</span>
                    <span className="text-lg text-slate-500 mb-4">%</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${project?.health === 'On Track' ? 'bg-success' : project?.health === 'At Risk' ? 'bg-warning' : 'bg-error'}`}
                      style={{ width: `${project?.progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-8">{project?.health} — {project?.status}</p>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-24">
                  <h2 className="text-base font-semibold text-slate-800 mb-16 flex items-center gap-8">
                    <Icon name="Activity" size={16} className="text-primary" />
                    Quick Stats
                  </h2>
                  <div className="space-y-12">
                    <div className="flex items-center justify-between py-8 border-b border-slate-50">
                      <span className="text-xs text-slate-500">Status</span>
                      <span className={`text-xs font-semibold px-8 py-4 rounded-full ${statusClass}`}>{project?.status}</span>
                    </div>
                    <div className="flex items-center justify-between py-8 border-b border-slate-50">
                      <span className="text-xs text-slate-500">Health</span>
                      <span className={`text-xs font-semibold px-8 py-4 rounded-full ${healthClass}`}>{project?.health}</span>
                    </div>
                    <div className="flex items-center justify-between py-8">
                      <span className="text-xs text-slate-500">Project ID</span>
                      <span className="text-xs font-medium text-slate-700">{project?.projectId}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ProjectProfilePage;
